import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import Lottie from "lottie-react";
import { HiX } from "react-icons/hi";
import { STREAK_BADGES, StreakBadge } from "../../constants/streakBadges";

interface StreakMilestonesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentStreak: number;
}

const StreakMilestonesDialog: React.FC<StreakMilestonesDialogProps> = ({
  isOpen,
  onClose,
  currentStreak,
}) => {
  const lastEarnedIndex = STREAK_BADGES.reduce<number>(
    (acc, b, i) => (currentStreak >= b.days ? i : acc),
    -1,
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-md bg-base-100 sm:rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-base-200 flex-shrink-0">
                  <div>
                    <Dialog.Title
                      as="h2"
                      className="text-base font-bold text-base-content"
                    >
                      Milestones
                    </Dialog.Title>
                    <p className="text-xs text-base-content/40 mt-0.5">
                      {lastEarnedIndex >= 0
                        ? `${lastEarnedIndex + 1} of ${STREAK_BADGES.length} earned`
                        : "No milestones earned yet"}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1.5 rounded-full text-base-content/40 hover:text-base-content/60 hover:bg-base-200 transition-colors"
                  >
                    <HiX className="w-4 h-4" />
                  </button>
                </div>

                {/* Current streak pill */}
                <div className="px-5 pt-4 pb-2 flex-shrink-0">
                  <div className="inline-flex items-center gap-1.5 bg-[#0096C7]/10 rounded-full px-3 py-1">
                    <span className="text-[11px] font-semibold text-[#0096C7]">
                      Current streak:
                    </span>
                    <span className="text-[11px] font-bold text-[#0096C7]">
                      {currentStreak} {currentStreak === 1 ? "day" : "days"}
                    </span>
                  </div>
                </div>

                {/* Badge grid — scrollable */}
                <div className="overflow-y-auto flex-1 px-4 pb-6 pt-2">
                  <div className="grid grid-cols-3 gap-3">
                    {STREAK_BADGES.map((badge: StreakBadge, index: number) => {
                      const earned = currentStreak >= badge.days;
                      const isLatest = index === lastEarnedIndex;
                      const daysLeft = badge.days - currentStreak;

                      return (
                        <div
                          key={badge.days}
                          className={`
                            relative flex flex-col items-center rounded-xl p-3 text-center transition-all
                            ${
                              earned
                                ? isLatest
                                  ? "bg-[#0096C7]/8 ring-2 ring-[#0096C7]/30"
                                  : "bg-base-200"
                                : "bg-base-200/60"
                            }
                          `}
                        >
                          {/* Latest badge marker */}
                          {isLatest && (
                            <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#0096C7] text-white text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full leading-none whitespace-nowrap">
                              Latest
                            </span>
                          )}

                          {/* Lottie + lock overlay */}
                          <div className="relative mb-1.5">
                            <div
                              className={`transition-all duration-300 ${
                                earned ? "" : "grayscale opacity-40"
                              }`}
                            >
                              <Lottie
                                animationData={badge.lottie}
                                loop={false}
                                autoplay={earned}
                                style={{
                                  width: isLatest ? 56 : 48,
                                  height: isLatest ? 56 : 48,
                                }}
                              />
                            </div>

                            {/* Lock icon for unearned */}
                            {!earned && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <svg
                                  className="w-4 h-4 text-base-content/40"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>

                          {/* Badge name */}
                          <p
                            className={`text-[11px] font-semibold leading-tight mb-0.5 ${
                              earned
                                ? "text-base-content"
                                : "text-base-content/40"
                            }`}
                          >
                            {badge.name}
                          </p>

                          {/* Description or "X days to go" */}
                          {earned ? (
                            <p className="text-[9px] text-base-content/40 leading-tight">
                              {badge.description}
                            </p>
                          ) : (
                            <p className="text-[9px] text-base-content/40 leading-tight">
                              {daysLeft} {daysLeft === 1 ? "day" : "days"} to go
                            </p>
                          )}

                          {/* Days threshold pill */}
                          <span
                            className={`mt-1.5 text-[8px] font-medium px-1.5 py-0.5 rounded-full ${
                              earned
                                ? "bg-[#0096C7]/15 text-[#0096C7]"
                                : "bg-base-300/60 text-base-content/40"
                            }`}
                          >
                            {badge.days}d
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default StreakMilestonesDialog;
